import React, { Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import Header from "components/ui/Header";
import LoadingSpinner from "components/ui/LoadingSpinner";

// Lazy load page components for code splitting
const LandingPage = React.lazy(() => import("pages/landing-page"));
const CommunityGuyWorld = React.lazy(() => import("pages/community-guy-world"));
const Web3DeveloperWorld = React.lazy(() => import("pages/web3-developer-world"));
const SoftwareDeveloperWorld = React.lazy(() => import("pages/software-developer-world"));
const SubmitTestimonialPage = React.lazy(() => import("pages/submit-testimonial"));
const AdminLogin = React.lazy(() => import("pages/admin"));
const AdminDashboard = React.lazy(() => import("pages/admin/dashboard"));
const NotFound = React.lazy(() => import("pages/NotFound"));
const MiniAppLanding = React.lazy(() => import("components/MiniAppLanding"));

// Custom fallback components for different pages
const PageFallback = ({ pageName }) => (
  <LoadingSpinner message={`Loading ${pageName}...`} />
);

const AdminFallback = () => (
  <LoadingSpinner message="Loading Admin Panel..." />
);

// Wrapper component for pages with Header
const PageWithHeader = ({ children }) => (
  <>
    <Header />
    {children}
  </>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Mini App specific route */}
          <Route 
            path="/miniapp" 
            element={
              <Suspense fallback={<PageFallback pageName="Mini App" />}>
                <MiniAppLanding />
              </Suspense>
            } 
          />
          
          {/* Public routes with lazy loading */}
          <Route 
            path="/" 
            element={
              <Suspense fallback={<PageFallback pageName="Home" />}>
                <PageWithHeader>
                  <LandingPage />
                </PageWithHeader>
              </Suspense>
            } 
          />
          <Route 
            path="/index.html" 
            element={
              <Suspense fallback={<PageFallback pageName="Home" />}>
                <PageWithHeader>
                  <LandingPage />
                </PageWithHeader>
              </Suspense>
            } 
          />
          <Route 
            path="/landing-page" 
            element={
              <Suspense fallback={<PageFallback pageName="Home" />}>
                <PageWithHeader>
                  <LandingPage />
                </PageWithHeader>
              </Suspense>
            } 
          />
          <Route 
            path="/community-guy-world" 
            element={
              <Suspense fallback={<PageFallback pageName="Community Guy World" />}>
                <PageWithHeader>
                  <CommunityGuyWorld />
                </PageWithHeader>
              </Suspense>
            } 
          />
          <Route 
            path="/web3-developer-world" 
            element={
              <Suspense fallback={<PageFallback pageName="Web3 Developer World" />}>
                <PageWithHeader>
                  <Web3DeveloperWorld />
                </PageWithHeader>
              </Suspense>
            } 
          />
          <Route 
            path="/software-developer-world" 
            element={
              <Suspense fallback={<PageFallback pageName="Software Developer World" />}>
                <PageWithHeader>
                  <SoftwareDeveloperWorld />
                </PageWithHeader>
              </Suspense>
            } 
          />
          <Route 
            path="/submit-testimonial" 
            element={
              <Suspense fallback={<PageFallback pageName="Submit Testimonial" />}>
                <PageWithHeader>
                  <SubmitTestimonialPage />
                </PageWithHeader>
              </Suspense>
            } 
          />
          
          {/* Admin routes with separate loading */}
          <Route 
            path="/admin" 
            element={
              <Suspense fallback={<AdminFallback />}>
                <AdminLogin />
              </Suspense>
            } 
          />
          <Route 
            path="/admin/dashboard/*" 
            element={
              <Suspense fallback={<AdminFallback />}>
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              </Suspense>
            } 
          />
          
          {/* 404 route */}
          <Route 
            path="*" 
            element={
              <Suspense fallback={<PageFallback pageName="Page" />}>
                <NotFound />
              </Suspense>
            } 
          />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;